import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, forkJoin, map, of } from 'rxjs';
import moment from 'moment';
import {
  CalendarDayViewBeforeRenderEvent,
  CalendarModule,
} from 'angular-calendar';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import IAppointment from '../../../_interfaces/IAppointment';
import ICalendar from '../../../_interfaces/ICalendar';
import IPatient from '../../../_interfaces/IPatients';
import IPatientHasTreatment from '../../../_interfaces/IPatientHasTreatment';
import IPayment from '../../../_interfaces/IPayment';
import IPaymentTypes from '../../../_interfaces/IPaymentTypes';
import ITreatment from '../../../_interfaces/ITreatment';
import IUser from '../../../_interfaces/IUser';
import { ITimeSlot } from '../../../_interfaces/ITimeSlot';
import IMarketingChannels from '../../../_interfaces/IMarketingChannels';

import { BaseComponent } from '../../base/base.component';
import { PatientSearcherComponent } from '../../patient-searcher/patient-searcher.component';
import { SelectorMarketingChannelsComponent } from '../../selectors/selector-marketing-channels/selector-marketing-channels.component';
import { TimeSlotSelectorComponent } from '../../selectors/time-slot-selector/time-slot-selector.component';
import { TreatmentManagerComponent } from './treatment-manager/treatment-manager.component';

import { AppointmentService } from '../../../_services/appointments/appointment.service';
import {
  AppointmentCreationService,
  AppointmentCreationDraft,
} from '../../../_services/appointments/appointment-creation.service';
import { CalendarAppointmentsService } from '../../../_services/calendars/calendar-appointments.service';
import { CalendarService } from '../../../_services/calendars/calendar.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { PatientsService } from '../../../_services/patients/patients.service';
import { PaymentsService } from '../../../_services/payments/payments.service';
import { SessionService } from '../../../_services/session/session.service';
import { SitesService } from '../../../_services/sites/sites.service';
import { TimeSlotService } from '../../../_services/time-slot/time-slot.service';
import { TreatmentsService } from '../../../_services/treatments/treatments.service';


type PatientSelectionMode = 'existing' | 'new';
type WizardStepKey =
  | 'patient'
  | 'treatments'
  | 'professionals'
  | 'payment'
  | 'time'
  | 'review';

interface TreatmentPaymentDraft {
  treatmentId: number;
  treatmentName: string;
  treatmentPrice: number;
  isAlreadyPaid: boolean;
  shouldCreatePayment: boolean;
  paymentTypeId: number | null;
  paymentDateTime: Date | null;
  sitesId: number;
}

interface DayTimelineItem {
  key: string;
  title: string;
  timeLabel: string;
  top: number;
  height: number;
  selected: boolean;
}

@Component({
  standalone: true,
  selector: 'app-form-create-appointment',
  templateUrl: './form-create-appointment.component.html',
  styleUrls: ['./form-create-appointment.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    MessageModule,
    TextareaModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    CalendarModule,
    TranslatePipe,
    SelectorMarketingChannelsComponent,
    PatientSearcherComponent,
    TreatmentManagerComponent,
    TimeSlotSelectorComponent,
  ],
})
export class FormCreateAppointmentComponent extends BaseComponent implements OnInit {
  private static readonly DEFAULT_DURATION_MINUTES = 30;
  private static readonly TAP_DRAG_THRESHOLD_PX = 8;
  readonly dayViewHourSegmentHeight = 30;

  @ViewChild('patientSearcher')
  public patientSearcher!: PatientSearcherComponent;
  @ViewChild('dayCalendarScrollContainer')
  public dayCalendarScrollContainer?: ElementRef<HTMLDivElement>;

  // Core
  public patient: IPatient = this.buildEmptyPatient();
  public patientDraft: IPatient = this.buildEmptyPatient();
  public patientSelectionMode: PatientSelectionMode = 'existing';
  public patientOnInit = false;
  public patientEditMode = false;
  public patientDraftTouched = false;
  public isSavingPatient = false;

  public defaultCalendarOption: { code: number; name: string } | undefined =
    undefined;
  public calendar!: ICalendar;
  public calendars: ICalendar[] = [];

  public selectedTreatments: IPatientHasTreatment[] = [];
  public addTreatmentsNow = true;

  public selectedDateOnly: Date | null = null;
  public selectedTimeSlot: ITimeSlot | null = null;
  public dayAppointments: IAppointment[] = [];
  public isLoadingAppointments = false;
  public availableSlots: ITimeSlot[] = [];
  public optimalSlots: ITimeSlot[] = [];
  private pendingAutoScrollToSlot = false;
  private lastSelectedTreatmentsKey = '';
  private selectedCalendarSlotDurationMinutes: number | null = null;
  private preserveSelectedSlotStart = false;
  private dayViewPointerState:
    | {
        pointerId: number;
        startDate: Date;
        startY: number;
        segmentHeight: number;
        dragging: boolean;
        moved: boolean;
        target: HTMLElement | null;
      }
    | null = null;
  private suppressNextDaySegmentClick = false;

  public appointmentNotes = '';
  public appointmentTitle = '';
  public sendEmailReminder = false;
  public price = 0;
  public startDate: Date | undefined;
  public callback: () => void = () => undefined;

  public conflicts: IAppointment[] = [];

  // Duration
  public appointmentDurationMinutes =
    FormCreateAppointmentComponent.DEFAULT_DURATION_MINUTES;
  public calculatedDurationMinutes =
    FormCreateAppointmentComponent.DEFAULT_DURATION_MINUTES;
  public minutesPerTreatment =
    FormCreateAppointmentComponent.DEFAULT_DURATION_MINUTES;

  // Professionals step
  public professionals: Array<IUser & { fullName: string }> = [];
  public allowedProfessionalIdsByTreatmentId: Record<number, number[]> = {};
  public professionalAssignments: Record<number, number | null> = {};
  public globalProfessionalId: number | null = null;

  // Payments step
  public paymentTypes: IPaymentTypes[] = [];
  public paymentTemplate: { paymentTypeId: number | null; paymentDateTime: Date | null } = {
    paymentTypeId: null,
    paymentDateTime: null,
  };
  public treatmentPayments: TreatmentPaymentDraft[] = [];
  public genderOptions: Array<{ name: string; code: string }> = [];

  // Wizard
  readonly wizardOrder: WizardStepKey[] = [
    'patient',
    'treatments',
    'professionals',
    'payment',
    'time',
    'review',
  ];
  public currentStep: WizardStepKey = 'patient';

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public appointmentsService: AppointmentService,
    public patientService: PatientsService,
    public messageService: MessageService,
    public translate: TranslateService,
    public calendarService: CalendarAppointmentsService,
    public errorsService: ErrorsService,
    public session: SessionService,
    public calendarsDataService: CalendarService,
    public paymentsService: PaymentsService,
    public sitesService: SitesService,
    public treatmentsService: TreatmentsService,
    private timeSlotService: TimeSlotService,
    private appointmentCreationService: AppointmentCreationService,
  ) {
    super(session);
  }

  // ==========================================================================
  // Wizard helpers
  // ==========================================================================

  get visibleSteps(): WizardStepKey[] {
    return this.wizardOrder.filter((step) => {
      if (step === 'professionals') {
        return this.shouldShowProfessionalStep();
      }
      if (step === 'payment') {
        return this.shouldShowPaymentStep();
      }
      return true;
    });
  }

  get currentStepIndex(): number {
    return Math.max(0, this.visibleSteps.indexOf(this.currentStep));
  }

  get progressPercent(): number {
    const total = this.visibleSteps.length;
    if (!total) return 0;
    return Math.round(((this.currentStepIndex + 1) / total) * 100);
  }

  get canGoBack(): boolean {
    return this.currentStepIndex > 0;
  }

  get canGoNext(): boolean {
    if (this.currentStep === 'review') return false;
    return this.validateStep(this.currentStep);
  }

  get canSave(): boolean {
    return this.currentStep === 'review' && this.validateStep('review');
  }

  get canOfferEmailReminder(): boolean {
    return !!this.patient?.id && !!`${this.patient?.email || ''}`.trim();
  }

  get isSingleCalendar(): boolean {
    return (this.calendars?.length || 0) === 1;
  }

  goToPreviousStep(): void {
    if (!this.canGoBack) return;
    const prevIndex = this.currentStepIndex - 1;
    const prev = this.visibleSteps[prevIndex];
    if (prev) {
      this.currentStep = prev;
    }
  }

  goToNextStep(): void {
    if (this.currentStep === 'patient') {
      this.persistPatientStepAndContinue();
      return;
    }

    if (!this.canGoNext) {
      this.showValidationHintForStep(this.currentStep);
      return;
    }

    this.advanceToNextVisibleStep();
  }

  private advanceToNextVisibleStep(): void {
    const nextIndex = this.currentStepIndex + 1;
    const next = this.visibleSteps[nextIndex];
    if (next) {
      this.currentStep = next;
    }
  }

  private ensureValidCurrentStep(): void {
    if (!this.visibleSteps.includes(this.currentStep)) {
      this.currentStep = this.visibleSteps[this.visibleSteps.length - 1] || 'patient';
    }
  }

  private validateStep(step: WizardStepKey): boolean {
    switch (step) {
      case 'patient':
        return true;
      case 'treatments':
        return true;
      case 'professionals':
        return true;
      case 'payment':
        return this.validatePaymentStep();
      case 'time':
        return !!this.selectedTimeSlot;
      case 'review':
        return (
          !!this.calendar &&
          this.validateStep('payment') &&
          this.validateStep('time')
        );
      default:
        return true;
    }
  }

  private showValidationHintForStep(step: WizardStepKey): void {
    const detailByStep: Record<WizardStepKey, string> = {
      patient:
        'Puedes continuar sin paciente o completar sus datos ahora.',
      treatments: 'Revisa los tratamientos antes de continuar.',
      professionals: 'Puedes omitir este paso o asignar profesionales.',
      payment: 'Revisa método y fecha/hora en los tratamientos pendientes de cobro.',
      time: 'Selecciona una hora disponible para continuar.',
      review: 'Revisa los datos antes de guardar la cita.',
    };

    this.messageService.add({
      severity: 'warn',
      summary: 'Datos incompletos',
      detail: detailByStep[step],
    });
  }

  // ==========================================================================
  // Agenda + slots
  // ==========================================================================

  onDateSelected(date: Date): void {
    this.selectedDateOnly = date;
    this.selectedTimeSlot = null;
    this.preserveSelectedSlotStart = false;
    this.availableSlots = [];
    this.optimalSlots = [];
    this.pendingAutoScrollToSlot = true;
    this.loadAppointmentsForDay(date);
  }

  shiftSelectedDate(days: number): void {
    const base = this.selectedDateOnly ? moment(this.selectedDateOnly) : moment();
    this.onDateSelected(base.add(days, 'day').toDate());
  }

  onTimeSlotSelected(slot: ITimeSlot): void {
    this.selectedTimeSlot = slot;
    this.startDate = slot.startTime;
    this.preserveSelectedSlotStart = true;
    this.scrollCalendarToDate(slot.startTime);
  }

  private updateSelectedTimeSlotEndFromDuration(): void {
    const selectedStart = this.selectedTimeSlot?.startTime || this.startDate;
    if (!selectedStart || (!this.selectedTimeSlot && !this.preserveSelectedSlotStart)) {
      return;
    }

    const start = moment(selectedStart).seconds(0).milliseconds(0);
    const end = start.clone().add(this.appointmentDurationMinutes, 'minutes');
    this.selectedTimeSlot = {
      ...(this.selectedTimeSlot || {
        isOptimal: false,
        startsAfterAppointment: false,
      }),
      startTime: start.toDate(),
      endTime: end.toDate(),
      startTimeFormatted: start.format('HH:mm'),
      endTimeFormatted: end.format('HH:mm'),
      availableMinutes: this.appointmentDurationMinutes,
    };
    this.startDate = this.selectedTimeSlot.startTime;
  }

  onTimeSlotsComputed(result: {
    availableSlots: ITimeSlot[];
    optimalSlots: ITimeSlot[];
  } | null): void {
    this.availableSlots = result?.availableSlots || [];
    this.optimalSlots = result?.optimalSlots || [];

    if (this.selectedTimeSlot) {
      const matchingSlot = [...this.optimalSlots, ...this.availableSlots].find(
        (slot) =>
          moment(slot.startTime).isSame(this.selectedTimeSlot?.startTime, 'minute') &&
          moment(slot.endTime).isSame(this.selectedTimeSlot?.endTime, 'minute'),
      );
      if (matchingSlot) {
        this.selectedTimeSlot = matchingSlot;
      } else {
        const matchingStartSlot = [...this.optimalSlots, ...this.availableSlots].find(
          (slot) =>
            moment(slot.startTime).isSame(this.selectedTimeSlot?.startTime, 'minute'),
        );
        if (matchingStartSlot) {
          this.selectedTimeSlot = matchingStartSlot;
          this.startDate = matchingStartSlot.startTime;
        }
      }
    }

    if (this.pendingAutoScrollToSlot) {
      this.pendingAutoScrollToSlot = false;
      const preferredSlot =
        this.selectedTimeSlot || this.optimalSlots[0] || this.availableSlots[0] || null;
      if (preferredSlot) {
        this.scrollCalendarToDate(preferredSlot.startTime);
      } else {
        this.scrollCalendarToDate(
          moment(this.dayViewDate)
            .startOf('day')
            .set({ hour: this.dayViewStartHour, minute: 0, second: 0 })
            .toDate(),
        );
      }
    }
  }

  onDayHourSegmentClicked(date: Date): void {
    if (!this.isDateWithinActiveTimetable(date)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Fuera de horario',
        detail:
          'Ese tramo está fuera del horario del calendario seleccionado.',
      });
      return;
    }

    if (!this.availableSlots.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin huecos',
        detail: 'No hay huecos disponibles para la duración seleccionada.',
      });
      return;
    }

    const clicked = moment(date).seconds(0).milliseconds(0);
    const exact = this.availableSlots.find((slot) =>
      moment(slot.startTime).isSame(clicked, 'minute'),
    );
    if (exact) {
      this.onTimeSlotSelected(exact);
      return;
    }

    const nextSlot = [...this.availableSlots]
      .filter((slot) => moment(slot.startTime).isSameOrAfter(clicked))
      .sort(
        (a, b) =>
          moment(a.startTime).valueOf() - moment(b.startTime).valueOf(),
      )[0];

    if (nextSlot) {
      this.onTimeSlotSelected(nextSlot);
      this.messageService.add({
        severity: 'info',
        summary: 'Hora ajustada',
        detail: `No había hueco exacto. Se seleccionó el siguiente disponible: ${nextSlot.startTimeFormatted}.`,
      });
      return;
    }

    const lastSlot = [...this.availableSlots].sort(
      (a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf(),
    )[this.availableSlots.length - 1];

    if (lastSlot) {
      this.onTimeSlotSelected(lastSlot);
    }
  }

  onDayHourSegmentPointerDown(
    event: PointerEvent,
    segmentDate: Date,
    segmentHeight: number,
  ): void {
    if (!this.calendar || !this.selectedDateOnly) {
      return;
    }

    if (event.pointerType === 'touch') {
      this.clearDayViewPointerState();
      return;
    }

    const target = event.currentTarget as HTMLElement | null;
    this.dayViewPointerState = {
      pointerId: event.pointerId,
      startDate: new Date(segmentDate),
      startY: event.clientY,
      segmentHeight: segmentHeight || 30,
      dragging: false,
      moved: false,
      target,
    };
    this.safeSetPointerCapture(target, event.pointerId);
  }

  onDayHourSegmentClickFallback(event: MouseEvent, date: Date): void {
    if (this.suppressNextDaySegmentClick) {
      this.suppressNextDaySegmentClick = false;
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.clearDayViewPointerState();
    this.onDayHourSegmentClicked(date);
  }

  @HostListener('document:pointermove', ['$event'])
  onDayHourSegmentPointerMove(event: PointerEvent): void {
    if (!this.dayViewPointerState || event.pointerId !== this.dayViewPointerState.pointerId) {
      return;
    }

    const deltaY = event.clientY - this.dayViewPointerState.startY;
    const absDeltaY = Math.abs(deltaY);
    if (absDeltaY <= FormCreateAppointmentComponent.TAP_DRAG_THRESHOLD_PX) {
      return;
    }

    event.preventDefault();
    this.dayViewPointerState.dragging = true;
    this.dayViewPointerState.moved = true;

    const segmentMinutes = Math.max(1, Math.round(60 / this.dayViewHourSegments));
    const slotCount = Math.max(
      1,
      Math.ceil(absDeltaY / Math.max(this.dayViewPointerState.segmentHeight, 1)),
    );
    const durationMinutes = slotCount * segmentMinutes;
    const anchor = moment(this.dayViewPointerState.startDate)
      .seconds(0)
      .milliseconds(0);
    const start = deltaY < 0
      ? anchor.clone().subtract(durationMinutes, 'minutes')
      : anchor;
    const end = deltaY < 0
      ? anchor
      : anchor.clone().add(durationMinutes, 'minutes');

    this.applyDraggedTimeSlot(start.toDate(), end.toDate(), { silent: true });
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onDayHourSegmentPointerUp(event: PointerEvent): void {
    if (!this.dayViewPointerState || event.pointerId !== this.dayViewPointerState.pointerId) {
      return;
    }

    this.safeReleasePointerCapture(this.dayViewPointerState.target, event.pointerId);
    const pointerState = this.dayViewPointerState;
    this.clearDayViewPointerState();
    this.suppressNextDaySegmentClick = true;
    setTimeout(() => {
      this.suppressNextDaySegmentClick = false;
    }, 0);

    if (!pointerState.dragging && !pointerState.moved) {
      this.onDayHourSegmentClicked(pointerState.startDate);
      return;
    }

    if (this.selectedTimeSlot) {
      this.scrollCalendarToDate(this.selectedTimeSlot.startTime);
    }
  }

  @HostListener('window:blur')
  @HostListener('document:visibilitychange')
  clearDayViewPointerState(): void {
    this.dayViewPointerState = null;
  }

  private safeSetPointerCapture(target: HTMLElement | null, pointerId: number): void {
    try {
      target?.setPointerCapture?.(pointerId);
    } catch {
      return;
    }
  }

  private safeReleasePointerCapture(target: HTMLElement | null, pointerId: number): void {
    try {
      target?.releasePointerCapture?.(pointerId);
    } catch {
      return;
    }
  }

  private applyDraggedTimeSlot(
    start: Date,
    end: Date,
    options?: { silent?: boolean },
  ): void {
    const startMoment = moment.min(moment(start), moment(end)).seconds(0).milliseconds(0);
    const endMoment = moment.max(moment(start), moment(end)).seconds(0).milliseconds(0);
    if (!endMoment.isAfter(startMoment)) {
      return;
    }

    if (
      !this.isDateRangeWithinActiveTimetable(startMoment.toDate(), endMoment.toDate())
    ) {
      if (!options?.silent) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Fuera de horario',
          detail: 'Ese tramo está fuera del horario del calendario seleccionado.',
        });
      }
      return;
    }

    const durationMinutes = endMoment.diff(startMoment, 'minutes');
    this.selectedCalendarSlotDurationMinutes = durationMinutes;
    this.calculatedDurationMinutes = durationMinutes;
    this.appointmentDurationMinutes = durationMinutes;
    this.minutesPerTreatment = this.selectedTreatments.length
      ? Math.max(1, Math.round(durationMinutes / this.selectedTreatments.length))
      : durationMinutes;
    this.selectedTimeSlot = {
      startTime: startMoment.toDate(),
      endTime: endMoment.toDate(),
      startTimeFormatted: startMoment.format('HH:mm'),
      endTimeFormatted: endMoment.format('HH:mm'),
      isOptimal: false,
      startsAfterAppointment: false,
      availableMinutes: durationMinutes,
    };
    this.startDate = this.selectedTimeSlot.startTime;
    this.preserveSelectedSlotStart = true;
  }

  private scrollCalendarToDate(targetDate: Date): void {
    const container = this.dayCalendarScrollContainer?.nativeElement;
    if (!container || !targetDate) return;

    const segmentsPerHour = this.dayViewHourSegments;
    const minutesPerSegment = 60 / segmentsPerHour;
    const startMinutes = this.dayViewStartHour * 60;
    const targetMinutes =
      moment(targetDate).hours() * 60 + moment(targetDate).minutes();
    const segmentIndex = Math.max(
      0,
      Math.floor((targetMinutes - startMinutes) / minutesPerSegment),
    );
    const offsetTop = Math.max(
      0,
      segmentIndex * this.dayViewHourSegmentHeight - 80,
    );

    setTimeout(() => {
      container.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }, 40);
  }

  get dayViewDate(): Date {
    return this.selectedDateOnly || moment().startOf('day').toDate();
  }

  get dayViewHourSegments(): number {
    const precision = Number(this.calendar?.precision || 15);
    if (precision <= 0) return 4;
    const segments = Math.floor(60 / precision);
    return segments > 0 ? segments : 4;
  }

  get dayViewStartHour(): number {
    const ranges = this.getActiveTimetableRangesForDayView();
    if (!ranges.length) return 8;
    return ranges.reduce((min, range) => Math.min(min, range.startHour), 23);
  }

  get dayViewStartMinute(): number {
    const ranges = this.getActiveTimetableRangesForDayView();
    if (!ranges.length) return 0;
    const minStartHour = ranges.reduce(
      (min, range) => Math.min(min, range.startHour),
      23,
    );
    return ranges
      .filter((range) => range.startHour === minStartHour)
      .reduce((min, range) => Math.min(min, range.startMinute), 59);
  }

  get dayViewEndHour(): number {
    const ranges = this.getActiveTimetableRangesForDayView();
    if (!ranges.length) return 20;
    const maxEnd = ranges.reduce((max, range) => {
      const total = range.endHour * 60 + range.endMinute;
      return Math.max(max, total);
    }, 0);
    return Math.floor(Math.min(maxEnd, 23 * 60 + 59) / 60);
  }

  get dayViewEndMinute(): number {
    const ranges = this.getActiveTimetableRangesForDayView();
    if (!ranges.length) return 59;
    const maxEnd = ranges.reduce((max, range) => {
      const total = range.endHour * 60 + range.endMinute;
      return Math.max(max, total);
    }, 0);
    return Math.min(maxEnd, 23 * 60 + 59) % 60;
  }

  onDayViewBeforeRender(renderEvent: CalendarDayViewBeforeRenderEvent): void {
    const ranges = this.getActiveTimetableRangesForDayView();
    const normalizedRanges = ranges
      .map((range) => ({
        start: range.startHour * 60 + range.startMinute,
        end: range.endHour * 60 + range.endMinute,
      }))
      .sort((a, b) => a.start - b.start);

    (renderEvent?.hourColumns || []).forEach((column: any) => {
      (column?.hours || []).forEach((hour: any) => {
        (hour?.segments || []).forEach((segment: any) => {
          const baseCss = `${segment.cssClass || ''}`
            .replace(/\bvc-out-of-schedule-segment\b/g, '')
            .replace(/\s+/g, ' ')
            .trim();

          if (!normalizedRanges.length) {
            segment.cssClass = baseCss;
            return;
          }

          const segmentDate = moment(segment?.date);
          const minuteOfDay = segmentDate.hours() * 60 + segmentDate.minutes();
          const isInside = normalizedRanges.some(
            (range) => minuteOfDay >= range.start && minuteOfDay < range.end,
          );
          segment.cssClass = isInside
            ? baseCss
            : `${baseCss} vc-out-of-schedule-segment`.trim();
        });
      });
    });
  }

  private parseHourValue(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.min(23, Math.floor(value)));
    }
    if (typeof value === 'string') {
      const firstChunk = value.trim().includes(':')
        ? value.trim().split(':')[0]
        : value.trim();
      const parsed = Number(firstChunk);
      if (Number.isFinite(parsed)) {
        return Math.max(0, Math.min(23, Math.floor(parsed)));
      }
    }
    return fallback;
  }

  private parseMinuteValue(value: unknown, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.min(59, Math.floor(value)));
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const parts = trimmed.split(':');
      if (parts.length > 1) {
        const parsed = Number(parts[1]);
        if (Number.isFinite(parsed)) {
          return Math.max(0, Math.min(59, Math.floor(parsed)));
        }
      }
    }
    return fallback;
  }

  private getActiveTimetableRangesForDayView(): Array<{
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
  }> {
    const timetables = this.calendar?.timetables || [];
    const day = moment(this.dayViewDate).day();
    const isoDay = moment(this.dayViewDate).isoWeekday();

    return timetables
      .filter((row: any) => {
        const weekday = Number(row?.weekdays_id);
        const active = Number(row?.active) === 1 || row?.active === true;
        return active && (weekday === day || weekday === isoDay);
      })
      .map((row: any) => ({
        startHour: this.parseHourValue(row?.start_hour, 8),
        startMinute: this.parseMinuteValue(row?.start_minute, 0),
        endHour: this.parseHourValue(row?.end_hour, 20),
        endMinute: this.parseMinuteValue(row?.end_minute, 0),
      }))
      .filter((range) => {
        const start = range.startHour * 60 + range.startMinute;
        const end = range.endHour * 60 + range.endMinute;
        return end > start;
      })
      .sort(
        (a, b) =>
          a.startHour * 60 +
          a.startMinute -
          (b.startHour * 60 + b.startMinute),
      );
  }

  private isDateWithinActiveTimetable(date: Date): boolean {
    const ranges = this.getActiveTimetableRangesForDayView();
    if (!ranges.length) {
      return true;
    }
    const minuteOfDay = moment(date).hours() * 60 + moment(date).minutes();
    return ranges.some((range) => {
      const start = range.startHour * 60 + range.startMinute;
      const end = range.endHour * 60 + range.endMinute;
      return minuteOfDay >= start && minuteOfDay < end;
    });
  }

  private isDateRangeWithinActiveTimetable(start: Date, end: Date): boolean {
    const ranges = this.getActiveTimetableRangesForDayView();
    if (!ranges.length) {
      return true;
    }
    const startMinute = moment(start).hours() * 60 + moment(start).minutes();
    const endMinute = moment(end).hours() * 60 + moment(end).minutes();
    return ranges.some((range) => {
      const rangeStart = range.startHour * 60 + range.startMinute;
      const rangeEnd = range.endHour * 60 + range.endMinute;
      return startMinute >= rangeStart && endMinute <= rangeEnd;
    });
  }

  get dayTimelineItems(): DayTimelineItem[] {
    const startMinutes = this.dayViewStartHour * 60 + this.dayViewStartMinute;
    const endMinutes = this.dayViewEndHour * 60 + this.dayViewEndMinute;
    const pixelsPerMinute =
      this.dayViewHourSegmentHeight / (60 / this.dayViewHourSegments);

    const buildItem = (
      key: string,
      title: string,
      startValue: Date | string,
      endValue: Date | string,
      selected: boolean,
    ): DayTimelineItem | null => {
      const start = moment(startValue);
      const end = moment(endValue);
      if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return null;

      const itemStartMinutes = start.hours() * 60 + start.minutes();
      const itemEndMinutes = end.hours() * 60 + end.minutes();
      const visibleStart = Math.max(startMinutes, itemStartMinutes);
      const visibleEnd = Math.min(endMinutes, itemEndMinutes);
      if (visibleEnd <= visibleStart) return null;

      return {
        key,
        title,
        timeLabel: `${start.format('HH:mm')} - ${end.format('HH:mm')}`,
        top: (visibleStart - startMinutes) * pixelsPerMinute,
        height: Math.max(22, (visibleEnd - visibleStart) * pixelsPerMinute),
        selected,
      };
    };

    const appointments = (this.dayAppointments || [])
      .map((appointment, index) =>
        buildItem(
          `appointment-${appointment.id || index}`,
          appointment.name || 'Cita',
          appointment.start,
          appointment.end,
          false,
        ),
      )
      .filter((item): item is DayTimelineItem => !!item);

    if (!this.selectedTimeSlot) return appointments;

    const selected = buildItem(
      'selected-time-slot',
      'Hueco seleccionado',
      this.selectedTimeSlot.startTime,
      this.selectedTimeSlot.endTime,
      true,
    );
    return selected ? [...appointments, selected] : appointments;
  }

  trackDayTimelineItem(_: number, item: DayTimelineItem): string {
    return item.key;
  }

  onDurationChanged(durationMinutes: number): void {
    this.appointmentDurationMinutes = durationMinutes;
  }

  loadAppointmentsForDay(date: Date): void {
    if (!this.calendar || !date) {
      this.dayAppointments = [];
      return;
    }

    this.isLoadingAppointments = true;
    const startOfDay = moment(date).startOf('day').format('DD-MM-YYYY HH:mm:ss');
    const endOfDay = moment(date).endOf('day').format('DD-MM-YYYY HH:mm:ss');

    this.appointmentsService
      .getAppointmentsByCalendar(this.customers_id, this.site, this.calendar, {
        start: startOfDay,
        end: endOfDay,
      })
      .subscribe({
        next: (appointments: IAppointment[]) => {
          this.dayAppointments = appointments || [];
          this.isLoadingAppointments = false;
        },
        error: () => {
          this.dayAppointments = [];
          this.isLoadingAppointments = false;
        },
      });
  }

  updateAppointmentDuration(): void {
    if (this.selectedCalendarSlotDurationMinutes) {
      this.calculatedDurationMinutes = this.selectedCalendarSlotDurationMinutes;
      this.appointmentDurationMinutes = this.selectedCalendarSlotDurationMinutes;
      this.minutesPerTreatment = this.selectedTreatments.length
        ? Math.max(
            1,
            Math.round(
              this.selectedCalendarSlotDurationMinutes / this.selectedTreatments.length,
            ),
          )
        : this.selectedCalendarSlotDurationMinutes;
      this.updateSelectedTimeSlotEndFromDuration();
      return;
    }

    this.calculatedDurationMinutes = this.timeSlotService.calculateDurationFromTreatments(
      this.selectedTreatments,
      FormCreateAppointmentComponent.DEFAULT_DURATION_MINUTES,
    );
    this.appointmentDurationMinutes = this.calculatedDurationMinutes;
    this.minutesPerTreatment = this.selectedTreatments.length
      ? Math.max(1, Math.round(this.calculatedDurationMinutes / this.selectedTreatments.length))
      : FormCreateAppointmentComponent.DEFAULT_DURATION_MINUTES;
    this.updateSelectedTimeSlotEndFromDuration();
  }

  handleCalendarChange = (calendar: ICalendar): void => {
    const previousCalendarId = this.calendar?.id;
    this.calendar = calendar;

    if (previousCalendarId !== calendar?.id) {
      if (this.selectedDateOnly) {
        this.loadAppointmentsForDay(this.selectedDateOnly);
      }
      if (previousCalendarId !== undefined) {
        this.selectedTimeSlot = null;
        this.preserveSelectedSlotStart = false;
      }
    }
  };

  private loadCalendarsForSite(): void {
    this.calendarsDataService.get(this.customers_id, this.site).subscribe({
      next: (calendars: ICalendar[]) => {
        this.calendars = calendars || [];

        if (!this.calendar && this.calendars[0]) {
          this.calendar = this.calendars[0];
        }

        if (this.calendar) {
          this.defaultCalendarOption = {
            code: this.calendar.id,
            name: this.calendar.name,
          };
        }

        this.applyPreferredCalendarFromSelectedTreatments();

        if (this.calendar && this.selectedDateOnly) {
          this.loadAppointmentsForDay(this.selectedDateOnly);
        }
      },
      error: () => {
        this.calendars = [];
      },
    });
  }

  // ==========================================================================
  // Patient step
  // ==========================================================================

  private buildEmptyPatient(): IPatient {
    return {
      name: undefined,
      surname: undefined,
      email: undefined,
      gender: undefined,
      address: undefined,
      postcode: undefined,
      phone: undefined,
      fiscal_id: undefined,
      birthday: undefined,
      remarks: undefined,
      marketing_channels_id: undefined,
      marketingChannel: undefined,
    };
  }

  private normalizePatientPayload(source: IPatient): IPatient {
    const payload: IPatient = {
      name: `${source?.name || ''}`.trim(),
      surname: `${source?.surname || ''}`.trim() || undefined,
      email: `${source?.email || ''}`.trim() || undefined,
      phone: `${source?.phone || ''}`.trim() || undefined,
      gender: `${source?.gender || ''}`.trim() || undefined,
      address: `${source?.address || ''}`.trim() || undefined,
      postcode: `${source?.postcode || ''}`.trim() || undefined,
      fiscal_id: `${source?.fiscal_id || ''}`.trim() || undefined,
      birthday: source?.birthday || undefined,
      remarks: `${source?.remarks || ''}`.trim() || undefined,
      marketing_channels_id:
        Number(source?.marketing_channels_id || 0) || undefined,
      marketingChannel: source?.marketingChannel || undefined,
    };
    return payload;
  }

  private copyPatientToDraft(patient: IPatient): void {
    this.patientDraft = {
      ...this.buildEmptyPatient(),
      ...patient,
      birthday: patient?.birthday ? moment(patient.birthday).toDate() : undefined,
    };
    this.patientDraftTouched = false;
  }

  changePatientMode(mode: PatientSelectionMode): void {
    this.patientSelectionMode = mode;
    this.patientEditMode = false;
    this.patientDraftTouched = false;

    if (mode === 'new') {
      this.patient = this.buildEmptyPatient();
      this.clearTreatmentsAndSelections();
      this.patientDraft = this.buildEmptyPatient();
      return;
    }

    if (!this.patient?.id) {
      this.patientDraft = this.buildEmptyPatient();
    } else {
      this.copyPatientToDraft(this.patient);
    }
  }

  managePatient(patient?: IPatient): void {
    if (!patient?.id) return;

    this.patientSelectionMode = 'existing';
    this.patient = patient;
    this.copyPatientToDraft(patient);
    this.patientEditMode = false;
    this.loadTreatmentsForPatient();
  }

  toggleExistingPatientEdition(): void {
    if (!this.patient?.id) return;
    this.patientEditMode = !this.patientEditMode;
    if (this.patientEditMode) {
      this.copyPatientToDraft(this.patient);
    }
  }

  onPatientDraftInput(): void {
    this.patientDraftTouched = true;
  }

  onPatientMarketingChannelChange(selected: IMarketingChannels): void {
    this.patientDraft.marketingChannel = selected;
    this.patientDraft.marketing_channels_id = Number(selected?.id || 0) || undefined;
    this.patientDraftTouched = true;
  }

  private persistPatientStepAndContinue(): void {
    if (this.isSavingPatient) return;

    if (this.patientSelectionMode === 'existing') {
      if (!this.patient?.id) {
        this.advanceToNextVisibleStep();
        return;
      }

      if (this.patientEditMode && this.patientDraftTouched) {
        const payload = this.normalizePatientPayload(this.patientDraft);
        if (!`${payload.name || ''}`.trim()) {
          this.showValidationHintForStep('patient');
          return;
        }

        this.isSavingPatient = true;
        this.patientService
          .update(this.customers_id, +this.site.id, +this.patient.id, payload)
          .subscribe({
            next: () => {
              this.isSavingPatient = false;
              this.patientEditMode = false;
              this.patientDraftTouched = false;
              this.applySavedPatient({ ...this.patient, ...payload, id: this.patient.id });
              this.advanceToNextVisibleStep();
            },
            error: () => {
              this.isSavingPatient = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron guardar los datos del paciente.',
              });
            },
          });
        return;
      }

      this.advanceToNextVisibleStep();
      return;
    }

    const payload = this.normalizePatientPayload(this.patientDraft);
    if (!`${payload.name || ''}`.trim()) {
      this.advanceToNextVisibleStep();
      return;
    }

    this.isSavingPatient = true;
    this.patientService.createPatient(this.customers_id, +this.site.id, payload).subscribe({
      next: (created: any) => {
        const createdId = Number(created?.id || created?.patients_id || 0);
        if (createdId > 0) {
          this.applySavedPatient({ ...payload, id: createdId } as IPatient);
          this.isSavingPatient = false;
          this.advanceToNextVisibleStep();
          return;
        }

        this.assignCreatedPatientFromSearch(payload, 0, () => {
          this.isSavingPatient = false;
          this.advanceToNextVisibleStep();
        });
      },
      error: () => {
        this.isSavingPatient = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo crear el paciente.',
        });
      },
    });
  }

  continueWithoutPatient(): void {
    this.clearPatientSelection();
    this.patientSelectionMode = 'existing';
    this.patientEditMode = false;
    this.patientDraftTouched = false;
    this.advanceToNextVisibleStep();
  }

  private normalizePatientValue(value: unknown): string {
    return `${value || ''}`.trim().toLowerCase();
  }

  private pickBestCreatedPatient(patients: IPatient[], target: IPatient): IPatient | undefined {
    if (!patients?.length) return undefined;

    const targetEmail = this.normalizePatientValue(target?.email);
    const targetPhone = this.normalizePatientValue(target?.phone);
    const targetFullName = this.normalizePatientValue(
      `${target?.name || ''} ${target?.surname || ''}`,
    );

    if (targetEmail) {
      const byEmail = patients.find(
        (patient) => this.normalizePatientValue(patient.email) === targetEmail,
      );
      if (byEmail) return byEmail;
    }

    if (targetPhone) {
      const byPhone = patients.find(
        (patient) => this.normalizePatientValue(patient.phone) === targetPhone,
      );
      if (byPhone) return byPhone;
    }

    if (targetFullName) {
      const byName = patients.find(
        (patient) =>
          this.normalizePatientValue(`${patient.name || ''} ${patient.surname || ''}`) ===
          targetFullName,
      );
      if (byName) return byName;
    }

    return [...patients]
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0))
      .at(0);
  }

  private assignCreatedPatientFromSearch(
    candidate: IPatient,
    attempt = 0,
    onDone?: () => void,
  ): void {
    const searchTerm = `${candidate?.email || candidate?.phone || candidate?.name || ''}`.trim();
    if (!searchTerm) {
      onDone?.();
      return;
    }

    this.patientService.getBySite(this.customers_id, +this.site.id, { search: searchTerm }).subscribe({
      next: (patients: IPatient[]) => {
        const matched = this.pickBestCreatedPatient(patients, candidate);
        if (matched?.id) {
          this.applySavedPatient(matched);
          onDone?.();
          return;
        }

        if (attempt < 3) {
          setTimeout(() => this.assignCreatedPatientFromSearch(candidate, attempt + 1, onDone), 250);
          return;
        }

        onDone?.();
      },
      error: () => {
        if (attempt < 3) {
          setTimeout(() => this.assignCreatedPatientFromSearch(candidate, attempt + 1, onDone), 250);
          return;
        }

        onDone?.();
      },
    });
  }

  private applySavedPatient(saved?: IPatient): void {
    const patientId = Number(saved?.id || this.patient?.id || 0);
    if (!patientId) return;

    this.patientService.get(this.customers_id, patientId).subscribe({
      next: (patient: IPatient) => {
        this.patient = patient;
        this.patientSelectionMode = 'existing';
        this.copyPatientToDraft(patient);
        this.patientEditMode = false;
        this.loadTreatmentsForPatient();
      },
      error: () => {
        const fallback = {
          ...this.patient,
          ...(saved || {}),
          id: patientId,
        } as IPatient;
        this.patient = fallback;
        this.patientSelectionMode = 'existing';
        this.copyPatientToDraft(fallback);
        this.patientEditMode = false;
        this.loadTreatmentsForPatient();
      },
    });
  }

  clearPatientSelection(): void {
    this.patient = this.buildEmptyPatient();
    this.patientDraft = this.buildEmptyPatient();
    this.patientEditMode = false;
    this.patientDraftTouched = false;
    this.clearTreatmentsAndSelections();
  }

  private clearTreatmentsAndSelections(): void {
    this.selectedTreatments = [];
    this.lastSelectedTreatmentsKey = '';
    this.selectedTimeSlot = null;
    this.preserveSelectedSlotStart = false;
    this.updateAppointmentDuration();
    this.syncProfessionalAssignmentsFromSelection();
    this.syncTreatmentPaymentsFromSelection();
    this.ensureValidCurrentStep();
  }

  getPatientInitials(): string {
    if (!this.patient?.name) return '?';
    const first = this.patient.name?.charAt(0) || '';
    const last = this.patient.surname?.charAt(0) || '';
    return (first + last).toUpperCase();
  }

  // ==========================================================================
  // Treatments step
  // ==========================================================================

  onAddTreatmentsNowChange(value: boolean): void {
    this.addTreatmentsNow = !!value;
    if (!this.addTreatmentsNow) {
      this.selectedTreatments = [];
      this.lastSelectedTreatmentsKey = '';
      this.selectedTimeSlot = null;
      this.preserveSelectedSlotStart = false;
      this.updateAppointmentDuration();
      this.syncProfessionalAssignmentsFromSelection();
      this.syncTreatmentPaymentsFromSelection();
      this.ensureValidCurrentStep();
    }
  }

  continueWithoutTreatments(): void {
    this.onAddTreatmentsNowChange(false);
    if (this.currentStep === 'treatments') {
      this.advanceToNextVisibleStep();
    }
  }

  onTreatmentsSelected(treatments: IPatientHasTreatment[]): void {
    const nextTreatments = treatments || [];
    const nextSelectionKey = this.buildSelectedTreatmentsKey(nextTreatments);
    const selectionChanged = nextSelectionKey !== this.lastSelectedTreatmentsKey;

    this.lastSelectedTreatmentsKey = nextSelectionKey;
    this.selectedTreatments = nextTreatments;
    this.selectedTimeSlot = null;
    this.updateAppointmentDuration();
    this.syncProfessionalAssignmentsFromSelection();
    this.syncTreatmentPaymentsFromSelection();
    if (selectionChanged) {
      this.applyPreferredCalendarFromSelectedTreatments();
    }
    this.ensureValidCurrentStep();
  }

  public loadTreatmentsForPatient(): void {
    if (!this.patient?.id) {
      this.clearTreatmentsAndSelections();
      return;
    }

    this.patientService
      .getTreatments(this.customers_id, this.patient.id, +this.site?.id || undefined)
      .subscribe({
        next: (treatments: IPatientHasTreatment[]) => {
          const available = (treatments || []).filter((treatment) => !treatment.appointment);
          const selectedIds = new Set(this.selectedTreatments.map((item) => Number(item.id || 0)));
          this.selectedTreatments = available.filter((item) => selectedIds.has(Number(item.id || 0)));
          this.lastSelectedTreatmentsKey = this.buildSelectedTreatmentsKey(
            this.selectedTreatments,
          );
          this.updateAppointmentDuration();
          this.syncProfessionalAssignmentsFromSelection();
          this.syncTreatmentPaymentsFromSelection();
          this.ensureValidCurrentStep();
        },
        error: () => {
          this.clearTreatmentsAndSelections();
        },
      });
  }

  // ==========================================================================
  // Professionals step
  // ==========================================================================

  private shouldShowProfessionalStep(): boolean {
    return (this.selectedTreatments?.length || 0) > 0;
  }

  get canManagePayments(): boolean {
    return this.session.canManagePayments(this.customer, this.site);
  }

  skipProfessionalsAndContinue(): void {
    this.selectedTreatments.forEach((treatment) => {
      const treatmentId = Number(treatment.id || 0);
      if (treatmentId > 0) {
        this.professionalAssignments[treatmentId] = null;
      }
    });
    this.globalProfessionalId = null;
    this.goToNextStep();
  }

  applyGlobalProfessionalToAll(): void {
    const globalId = Number(this.globalProfessionalId || 0) || null;
    this.selectedTreatments.forEach((treatment) => {
      const treatmentId = Number(treatment.id || 0);
      const options = this.getProfessionalOptionsForTreatment(treatment);
      const optionIds = new Set(options.map((option) => Number(option.id || 0)));
      if (globalId && optionIds.has(globalId)) {
        this.professionalAssignments[treatmentId] = globalId;
      } else if (!globalId) {
        this.professionalAssignments[treatmentId] = null;
      }
    });
  }

  onProfessionalAssigned(treatmentId: number, users_id: number | null): void {
    this.professionalAssignments[treatmentId] = users_id ? Number(users_id) : null;
  }

  private syncProfessionalAssignmentsFromSelection(): void {
    const selectedIds = new Set(this.selectedTreatments.map((item) => Number(item.id || 0)));

    Object.keys(this.professionalAssignments).forEach((id) => {
      if (!selectedIds.has(Number(id))) {
        delete this.professionalAssignments[Number(id)];
      }
    });

    this.selectedTreatments.forEach((treatment) => {
      const patientTreatmentId = Number(treatment.id || 0);
      if (
        Object.prototype.hasOwnProperty.call(this.professionalAssignments, patientTreatmentId)
      ) {
        const options = this.getProfessionalOptionsForTreatment(treatment);
        const optionIds = new Set(options.map((option) => Number(option.id || 0)));
        const current = Number(this.professionalAssignments[patientTreatmentId] || 0) || null;
        if (current && !optionIds.has(current)) {
          this.professionalAssignments[patientTreatmentId] = null;
        }
        return;
      }

      this.professionalAssignments[patientTreatmentId] = this.getDefaultProfessionalForTreatment(
        treatment,
      );
    });
  }

  private getDefaultProfessionalForTreatment(
    treatment: IPatientHasTreatment,
  ): number | null {
    const options = this.getProfessionalOptionsForTreatment(treatment);
    const optionIds = new Set(options.map((option) => Number(option.id || 0)));

    const assignedProfessionalId = Number(
      treatment?.users_id || treatment?.professional_users_id || 0,
    );
    if (assignedProfessionalId > 0 && optionIds.has(assignedProfessionalId)) {
      return assignedProfessionalId;
    }

    const treatmentDefaultId = Number(treatment?.treatment?.default_users_id || 0);
    if (treatmentDefaultId > 0 && optionIds.has(treatmentDefaultId)) {
      return treatmentDefaultId;
    }

    const calendarUsersId = Number(this.calendar?.users_id || 0);
    if (calendarUsersId > 0 && optionIds.has(calendarUsersId)) {
      return calendarUsersId;
    }

    return options.length === 1 ? Number(options[0].id || 0) : null;
  }

  getProfessionalOptionsForTreatment(
    treatment: IPatientHasTreatment,
  ): Array<IUser & { fullName: string }> {
    const treatmentCatalogId = Number(treatment?.treatments_id || 0);
    const allowedIds = this.allowedProfessionalIdsByTreatmentId[treatmentCatalogId] || [];

    if (!allowedIds.length) {
      return this.professionals;
    }

    return this.professionals.filter((professional) =>
      allowedIds.includes(Number(professional.id || 0)),
    );
  }

  // ==========================================================================
  // Payments step
  // ==========================================================================

  private shouldShowPaymentStep(): boolean {
    return this.canManagePayments && (this.selectedTreatments?.length || 0) > 0;
  }

  skipPaymentsAndContinue(): void {
    this.treatmentPayments = this.treatmentPayments.map((draft) => ({
      ...draft,
      shouldCreatePayment: false,
    }));
    this.goToNextStep();
  }

  updatePaymentTemplateType(paymentTypeId: number | null): void {
    this.paymentTemplate.paymentTypeId = paymentTypeId ? Number(paymentTypeId) : null;
  }

  updatePaymentTemplateDate(date: Date | null): void {
    this.paymentTemplate.paymentDateTime = date ? new Date(date) : null;
  }

  applyTemplateToAllPendingTreatments(): void {
    const nextType = Number(this.paymentTemplate.paymentTypeId || 0) || null;
    const nextDate = this.paymentTemplate.paymentDateTime
      ? new Date(this.paymentTemplate.paymentDateTime)
      : null;

    this.treatmentPayments = this.treatmentPayments.map((draft) => {
      if (draft.isAlreadyPaid || !draft.shouldCreatePayment) {
        return draft;
      }

      return {
        ...draft,
        paymentTypeId: nextType,
        paymentDateTime: nextDate ? new Date(nextDate) : null,
      };
    });
  }

  updateDraftType(draft: TreatmentPaymentDraft, paymentTypeId: number | null): void {
    draft.paymentTypeId = paymentTypeId ? Number(paymentTypeId) : null;
  }

  updateDraftDate(draft: TreatmentPaymentDraft, date: Date | null): void {
    draft.paymentDateTime = date ? new Date(date) : null;
  }

  private syncTreatmentPaymentsFromSelection(): void {
    const existingById = new Map(
      this.treatmentPayments.map((draft) => [draft.treatmentId, draft]),
    );

    this.treatmentPayments = this.selectedTreatments.map((treatment) => {
      const treatmentId = Number(treatment.id || 0);
      const existing = existingById.get(treatmentId);
      const isAlreadyPaid = this.isTreatmentPaid(treatment);

      return {
        treatmentId,
        treatmentName:
          treatment?.treatment?.name || this.translate.instant('COMMON.NO_NAME'),
        treatmentPrice: Number(treatment?.price || 0),
        isAlreadyPaid,
        shouldCreatePayment: !isAlreadyPaid ? existing?.shouldCreatePayment ?? false : false,
        paymentTypeId: existing?.paymentTypeId ?? this.paymentTemplate.paymentTypeId ?? null,
        paymentDateTime: existing?.paymentDateTime ?? this.paymentTemplate.paymentDateTime ?? null,
        sitesId: Number(this.site?.id || this.sites_id || 0),
      };
    });
  }

  private validatePaymentStep(): boolean {
    if (!this.shouldShowPaymentStep()) {
      return true;
    }

    return this.treatmentPayments
      .filter((draft) => draft.shouldCreatePayment && !draft.isAlreadyPaid)
      .every(
        (draft) => Number(draft.paymentTypeId || 0) > 0 && !!draft.paymentDateTime,
      );
  }

  private registerSelectedPayments() {
    const payables = this.treatmentPayments.filter(
      (draft) => draft.shouldCreatePayment && !draft.isAlreadyPaid,
    );

    if (!payables.length) {
      return of([] as Array<{ ok: boolean; treatmentId: number; error?: unknown }>);
    }

    const treatmentsById = new Map(
      this.selectedTreatments.map((treatment) => [Number(treatment.id || 0), treatment]),
    );

    const calls = payables.map((draft) => {
      const treatment = treatmentsById.get(draft.treatmentId);
      if (!treatment) {
        return of({ ok: false, treatmentId: draft.treatmentId, error: 'TREATMENT_NOT_FOUND' });
      }

      const payload: IPayment = {
        treatments_id: Number(treatment.id || draft.treatmentId),
        payments_types_id: Number(draft.paymentTypeId || 0),
        payment_date: moment(draft.paymentDateTime).utc().toDate(),
        sites_id: Number(draft.sitesId || this.site?.id || this.sites_id || 0),
        amount: Number(treatment.price || 0),
      };

      return this.paymentsService
        .registerPayment(
          this.customers_id,
          payload.sites_id,
          Number(treatment.id || draft.treatmentId),
          payload,
        )
        .pipe(
          map(() => ({ ok: true, treatmentId: draft.treatmentId })),
          catchError((error) => of({ ok: false, treatmentId: draft.treatmentId, error })),
        );
    });

    return forkJoin(calls);
  }

  private isTreatmentPaid(treatment: IPatientHasTreatment): boolean {
    const payments = treatment?.payments || [];
    const paidAmount = payments.reduce(
      (acc, payment) => acc + Number(payment?.amount || 0),
      0,
    );
    const price = Number(treatment?.price || 0);

    if (price <= 0) {
      return paidAmount > 0;
    }

    return paidAmount >= price - 0.001;
  }

  // ==========================================================================
  // Appointment creation
  // ==========================================================================

  private buildSelectedTreatmentsForCreate(): IPatientHasTreatment[] {
    if (!this.addTreatmentsNow) {
      return [];
    }

    return this.selectedTreatments.map((treatment) => {
      const treatmentId = Number(treatment.id || 0);
      const users_id = this.professionalAssignments[treatmentId] ?? null;

      return {
        ...treatment,
        users_id,
      };
    });
  }

  findConflicts() {
    const { start, end } = this.getSelectedRange();
    return this.appointmentCreationService.findConflicts(
      this.customers_id,
      this.site,
      this.calendar,
      start,
      end,
    );
  }

  registerEvent(checkConflict: boolean): void {
    if (!this.canSave) {
      this.showValidationHintForStep(this.currentStep);
      return;
    }

    const appointment = this.createAppointmentObject();
    if (!appointment) return;

    if (checkConflict) {
      this.resolveConflictsBeforeRegistering(appointment);
    } else {
      this.registerAppointment(appointment);
    }
  }

  createAppointmentObject(): IAppointment | null {
    if (!this.calendar) return null;

    const { start, end } = this.getSelectedRange();
    return this.appointmentCreationService.buildAppointment({
      calendar: this.calendar,
      start,
      end,
      patient: this.patient,
      title: this.appointmentTitle,
      notes: this.appointmentNotes,
      price: this.price,
    });
  }

  generateAppointmentName(): string | null {
    return this.appointmentCreationService.buildAppointmentName({
      start: this.getAppointmentStart(),
      patient: this.patient,
      title: this.appointmentTitle,
    });
  }

  getAppointmentStart(): Date {
    return this.getSelectedRange().start;
  }

  getAppointmentEnd(): Date {
    return this.getSelectedRange().end;
  }

  private getSelectedRange(): { start: Date; end: Date } {
    if (this.selectedTimeSlot) {
      return {
        start: moment(this.selectedTimeSlot.startTime)
          .set('seconds', 0)
          .utc()
          .toDate(),
        end: moment(this.selectedTimeSlot.endTime)
          .set('seconds', 0)
          .utc()
          .toDate(),
      };
    }

    const start = moment(this.startDate, 'DD-MM-YYYY HH:mm:ss')
      .set('seconds', 0)
      .utc()
      .toDate();
    const end = moment(start)
      .add(this.appointmentDurationMinutes, 'minutes')
      .toDate();

    return { start, end };
  }

  resolveConflictsBeforeRegistering(appointment: IAppointment): void {
    this.findConflicts().subscribe((appointments: IAppointment[]) => {
      if (appointments.length) {
        this.conflicts = appointments;
        return;
      }

      this.registerAppointment(appointment);
    });
  }

  registerAppointment(appointment: IAppointment): void {
    const selectedTreatments = this.buildSelectedTreatmentsForCreate();
    const draft: AppointmentCreationDraft = {
      calendar: this.calendar,
      start: this.getAppointmentStart(),
      end: this.getAppointmentEnd(),
      patient: this.patient,
      title: appointment.name,
      notes: this.appointmentNotes,
      price: this.price,
      treatments: selectedTreatments,
      sendEmailReminder: this.canOfferEmailReminder && this.sendEmailReminder,
    };

    this.appointmentCreationService
      .registerDraft(this.customers_id, this.site, draft)
      .subscribe({
        next: () => {
          this.registerSelectedPayments().subscribe((paymentResults) => {
            const failed = (paymentResults || []).filter((result) => !result.ok);

            this.calendarService.emitPatientAppointments$();
            this.callback();

            if (failed.length) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Cita creada con incidencias',
                detail:
                  'La cita se ha creado correctamente, pero algunos cobros no se registraron. Puedes completarlos desde la ficha de la cita.',
              });
            }

            this.ref.close();
          });
        },
        error: (error) => {
          const code = this.errorsService.getMessageFromSiteError(error);
          this.messageService.add({
            detail: this.translate.instant(`MESSAGES.APPOINTMENTS.ERROR.${code}`),
            summary: this.translate.instant('MESSAGES.APPOINTMENTS.ERROR.SUMMARY'),
            severity: 'error',
          });
        },
      });
  }

  // ==========================================================================
  // Review helpers
  // ==========================================================================

  get reviewDateLabel(): string {
    if (!this.selectedDateOnly) return '-';
    return moment(this.selectedDateOnly).format('dddd, DD MMMM YYYY');
  }

  get reviewTimeLabel(): string {
    if (!this.selectedTimeSlot) return '-';
    return `${this.selectedTimeSlot.startTimeFormatted} - ${this.selectedTimeSlot.endTimeFormatted}`;
  }

  get reviewDurationLabel(): string {
    return this.timeSlotService.formatDuration(this.appointmentDurationMinutes);
  }

  get reviewPaymentLabel(): string {
    if (!(this.selectedTreatments?.length || 0)) {
      return 'Sin tratamientos seleccionados';
    }

    if (!this.canManagePayments) {
      return 'Sin acceso para gestionar cobros en este login';
    }

    const toRegister = this.treatmentPayments.filter(
      (draft) => draft.shouldCreatePayment && !draft.isAlreadyPaid,
    ).length;

    if (!toRegister) {
      return 'Sin cobros nuevos en esta cita';
    }

    return `${toRegister} tratamiento(s) se cobrarán al guardar`;
  }

  get calendarOptions(): Array<{ id: number; name: string }> {
    return (this.calendars || []).map((calendar) => ({
      id: Number(calendar.id || 0),
      name: calendar.name || `Calendario ${calendar.id}`,
    }));
  }

  onWizardCalendarChange(calendarId: number | null): void {
    const selectedId = Number(calendarId || 0);
    if (!selectedId) return;

    const selectedCalendar = this.calendars.find(
      (calendar) => Number(calendar.id || 0) === selectedId,
    );
    if (!selectedCalendar) return;

    const previousCalendarId = Number(this.calendar?.id || 0);
    this.handleCalendarChange(selectedCalendar);

    if (previousCalendarId && previousCalendarId !== selectedId) {
      this.selectedTimeSlot = null;
      this.startDate = undefined;
      this.preserveSelectedSlotStart = false;
      this.messageService.add({
        severity: 'info',
        summary: 'Calendario actualizado',
        detail:
          'Selecciona de nuevo la hora disponible para este calendario antes de confirmar.',
      });
    }
  }

  private buildSelectedTreatmentsKey(
    treatments: IPatientHasTreatment[] = [],
  ): string {
    return (treatments || [])
      .map((treatment) => Number(treatment?.id || 0))
      .filter((id) => Number.isInteger(id) && id > 0)
      .sort((left, right) => left - right)
      .join('|');
  }

  private applyPreferredCalendarFromSelectedTreatments(): void {
    const selectedTreatment =
      this.selectedTreatments?.length === 1 ? this.selectedTreatments[0] : null;
    const preferredCalendarId = Number(
      selectedTreatment?.treatment?.booking_calendars_id || 0,
    );

    if (!Number.isInteger(preferredCalendarId) || preferredCalendarId <= 0) {
      return;
    }

    const preferredCalendar = (this.calendars || []).find(
      (calendar) => Number(calendar?.id || 0) === preferredCalendarId,
    );

    if (!preferredCalendar) {
      return;
    }

    if (Number(this.calendar?.id || 0) === preferredCalendarId) {
      return;
    }

    this.handleCalendarChange(preferredCalendar);
  }

  // ==========================================================================
  // Init loaders
  // ==========================================================================

  private loadProfessionalContext(): void {
    if (!this.customers_id || !this.site?.id) return;

    forkJoin({
      users: this.sitesService.getUsers(this.customers_id, this.site.id),
      treatments: this.treatmentsService.getFromSite(this.customers_id, {
        id: this.site.id,
      } as any),
    }).subscribe({
      next: ({ users, treatments }) => {
        this.professionals = (users || []).map((user) => ({
          ...user,
          fullName: user.surname ? `${user.name} ${user.surname}` : user.name,
        }));

        this.allowedProfessionalIdsByTreatmentId = {};
        (treatments || []).forEach((treatment: ITreatment) => {
          if (!treatment?.id) return;
          this.allowedProfessionalIdsByTreatmentId[treatment.id] = (
            treatment.allowed_users_ids || []
          )
            .map((value: any) => Number(value))
            .filter((value: number) => Number.isInteger(value) && value > 0);
        });

        this.syncProfessionalAssignmentsFromSelection();
      },
      error: () => {
        this.professionals = [];
        this.allowedProfessionalIdsByTreatmentId = {};
      },
    });
  }

  private resolvePaymentTypeLabel(type: IPaymentTypes): string {
    const rawName = `${type?.name || ''}`.trim();
    const normalizedRawName = rawName.replace(/^PAYMENT_TYPES\./i, '').trim();
    const canonical = normalizedRawName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();

    const aliasMap: Record<string, string> = {
      CREDIT_CARD: 'CARD',
      CREDITCARD: 'CARD',
      CARD: 'CARD',
      TARJETA: 'CARD',
      CASH: 'CASH',
      EFECTIVO: 'CASH',
      METALLIC: 'CASH',
      OTHER: 'OTHER',
      OTHER_METHOD: 'OTHER',
      OTHERMETHOD: 'OTHER',
      OTRO_METODO: 'OTHER',
      ALTRES: 'OTHER',
    };

    const aliasKey = aliasMap[canonical] || '';
    const upperNormalized = normalizedRawName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toUpperCase();

    const keys = [
      Number.isFinite(+type?.id) ? `PAYMENT_TYPES.${type.id}` : '',
      aliasKey ? `PAYMENT_TYPES.${aliasKey}` : '',
      `PAYMENT_TYPES.${normalizedRawName}`,
      `PAYMENT_TYPES.${rawName}`,
      upperNormalized ? `PAYMENT_TYPES.${upperNormalized}` : '',
    ].filter(Boolean);

    for (const key of keys) {
      const translated = this.translate.instant(key);
      if (translated && translated !== key) {
        return translated;
      }
    }

    return rawName || `${type?.id || ''}`;
  }

  private loadPaymentTypes(): void {
    this.paymentsService.getTypes(this.customers_id).subscribe({
      next: (types: IPaymentTypes[]) => {
        this.paymentTypes = (types || []).map((paymentType) => ({
          ...paymentType,
          name: this.resolvePaymentTypeLabel(paymentType),
        }));
      },
      error: () => {
        this.paymentTypes = [];
      },
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.genderOptions = [
      { name: this.translate.instant('COMMON.GENDER.FEMALE'), code: 'FEMALE' },
      { name: this.translate.instant('COMMON.GENDER.MALE'), code: 'MALE' },
      { name: this.translate.instant('COMMON.GENDER.OTHER'), code: 'OTHER' },
    ];

    const rawStartDate = this.config.data.startDate;
    const rawEndDate = this.config.data.endDate;
    const lockSelectedDuration = this.config.data.lockSelectedDuration === true;
    let initialMoment: moment.Moment;
    let initialEndMoment: moment.Moment | null = null;

    if (rawStartDate) {
      initialMoment = moment(rawStartDate, 'DD-MM-YYYY HH:mm', true);
      if (!initialMoment.isValid()) {
        initialMoment = moment(rawStartDate);
      }
    } else {
      initialMoment = moment();
    }

    if (rawEndDate) {
      initialEndMoment = moment(rawEndDate, 'DD-MM-YYYY HH:mm', true);
      if (!initialEndMoment.isValid()) {
        initialEndMoment = moment(rawEndDate);
      }
    }

    if (
      lockSelectedDuration &&
      initialEndMoment?.isValid() &&
      initialEndMoment.isAfter(initialMoment)
    ) {
      this.selectedCalendarSlotDurationMinutes = initialEndMoment.diff(
        initialMoment,
        'minutes',
      );
      this.updateAppointmentDuration();
    }

    this.selectedDateOnly = initialMoment.clone().startOf('day').toDate();
    this.startDate = initialMoment.toDate();

    if (rawStartDate && initialMoment.isValid()) {
      const startTime = initialMoment.toDate();
      const endTime = initialMoment
        .clone()
        .add(this.calculatedDurationMinutes, 'minutes')
        .toDate();
      this.selectedTimeSlot = {
        startTime,
        endTime,
        startTimeFormatted: initialMoment.format('HH:mm'),
        endTimeFormatted: initialMoment
          .clone()
          .add(this.calculatedDurationMinutes, 'minutes')
          .format('HH:mm'),
        isOptimal: false,
        startsAfterAppointment: false,
        availableMinutes: this.calculatedDurationMinutes,
      };
      this.preserveSelectedSlotStart = true;
    }

    const selectedCalendar = this.config.data.selectedCalendar;
    if (selectedCalendar) {
      this.defaultCalendarOption = {
        code: selectedCalendar.id,
        name: selectedCalendar.name,
      };
      this.calendar = selectedCalendar;
      this.loadAppointmentsForDay(this.selectedDateOnly);
    }

    if (this.config.data.patient) {
      this.patient = this.config.data.patient;
      this.patientOnInit = true;
      this.patientSelectionMode = 'existing';
      this.copyPatientToDraft(this.patient);
      this.loadTreatmentsForPatient();
    }

    this.callback = this.config.data.callback || (() => undefined);
    this.price = 0;

    this.updateAppointmentDuration();
    this.loadCalendarsForSite();
    this.loadProfessionalContext();
    this.loadPaymentTypes();
    this.syncProfessionalAssignmentsFromSelection();
    this.syncTreatmentPaymentsFromSelection();
  }
}
